import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';

// Tool catalog shown for reference (mirrors mcp/server.mjs).
const TOOL_GROUPS = [
  { group: 'Overview', tools: ['get_overview'] },
  { group: 'Users', tools: ['list_users', 'get_user'] },
  { group: 'Pro / Subscriptions', tools: ['list_pro_users'] },
  { group: 'Onboarding / Activation', tools: ['get_activation_funnel', 'get_user_acquisition'] },
  { group: 'Analytics', tools: ['get_analytics', 'get_scanned_food_users'] },
  { group: 'Promo Codes', tools: ['get_promo_codes', 'get_promo_code_trends'] },
  { group: 'Records', tools: ['list_records'] },
  { group: 'Teams', tools: ['list_teams', 'get_team'] },
  { group: 'AI Chats', tools: ['list_ai_chats', 'get_ai_chat', 'get_ai_insights'] },
  { group: 'Database', tools: ['list_tables', 'describe_table', 'sql_query'] },
];

const formatUptime = (sec) => {
  if (sec == null) return '—';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const randomToken = () => {
  const bytes = new Uint8Array(24);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
};

const McpSettings = () => {
  const { fetchWithAuth } = useAuth();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const [showToken, setShowToken] = useState(false);
  const [copied, setCopied] = useState(null);

  // Form fields (kept separate from live status so polling doesn't clobber edits)
  const [port, setPort] = useState('');
  const [authToken, setAuthToken] = useState('');
  const [publicUrl, setPublicUrl] = useState('');
  const formTouched = useRef(false);

  const load = useCallback(
    async (initial = false) => {
      try {
        const res = await fetchWithAuth('/api/admin/mcp');
        if (!res.ok) throw new Error((await res.json()).error || 'Failed to load MCP status');
        const data = await res.json();
        setState(data);
        setError(null);
        if (initial || !formTouched.current) {
          setPort(String(data.settings.port));
          setAuthToken(data.settings.authToken || '');
          setPublicUrl(data.settings.publicUrl || '');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    },
    [fetchWithAuth]
  );

  useEffect(() => {
    load(true);
    const timer = setInterval(() => load(false), 5000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const action = async (path, options = {}) => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetchWithAuth(path, options);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Request failed');
      setState(data);
      if (!formTouched.current) {
        setPort(String(data.settings.port));
        setAuthToken(data.settings.authToken || '');
        setPublicUrl(data.settings.publicUrl || '');
      }
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const toggle = () =>
    action(state?.running ? '/api/admin/mcp/stop' : '/api/admin/mcp/start', { method: 'POST' });

  const saveSettings = async () => {
    const ok = await action('/api/admin/mcp/settings', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ port: Number(port), authToken, publicUrl }),
    });
    if (ok) {
      formTouched.current = false;
      setSaveMsg(state?.running ? 'Saved — server restarted' : 'Saved');
      setTimeout(() => setSaveMsg(null), 3000);
    }
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(null), 1500);
  };

  if (loading) return <div className="loading">Loading...</div>;

  const running = state?.running;
  const healthy = state?.healthy;
  const crashed = !running && state?.lastExit && state.lastExit.code !== 0 && state.lastExit.signal == null;
  const statusColor = running ? (healthy === false ? '#f59e0b' : '#10b981') : crashed ? '#ef4444' : '#9ca3af';
  const statusText = running
    ? healthy === false
      ? 'Running (health check failing)'
      : 'Running'
    : crashed
      ? `Crashed (exit code ${state.lastExit.code})`
      : 'Stopped';

  const desktopConfig = JSON.stringify(
    { mcpServers: { 'kkal-admin': { command: 'node', args: ['/Users/GorA/Projects/KKAL-ADMIN_MY/mcp/server.mjs'] } } },
    null,
    2
  );

  return (
    <div>
      <h1 style={{ fontSize: '32px', fontWeight: '700', marginBottom: '24px' }}>MCP Server</h1>

      {error && (
        <div className="error" style={{ marginBottom: '16px' }}>
          {error}
        </div>
      )}

      {/* Status + toggle */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span
              style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                background: statusColor,
                display: 'inline-block',
                boxShadow: running ? `0 0 8px ${statusColor}` : 'none',
              }}
            />
            <div>
              <div style={{ fontSize: '18px', fontWeight: 600 }}>{statusText}</div>
              <div style={{ fontSize: '13px', color: '#6b7280' }}>
                {running
                  ? `PID ${state.pid} · uptime ${formatUptime(state.uptimeSec)} · ${state.url}`
                  : 'HTTP endpoint is offline. Stdio mode (Claude Code / Desktop) works independently of this toggle.'}
              </div>
            </div>
          </div>
          <button
            className="btn"
            onClick={toggle}
            disabled={busy || (!running && !state?.settings?.authToken)}
            title={
              !running && !state?.settings?.authToken
                ? 'Set an auth token first — the endpoint exposes all data'
                : undefined
            }
            style={{
              background: running ? '#ef4444' : '#10b981',
              color: '#fff',
              border: 'none',
              padding: '10px 24px',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: 600,
              cursor: busy ? 'wait' : !running && !state?.settings?.authToken ? 'not-allowed' : 'pointer',
              opacity: busy || (!running && !state?.settings?.authToken) ? 0.5 : 1,
            }}
          >
            {busy ? '…' : running ? 'Stop server' : 'Start server'}
          </button>
        </div>
      </div>

      <div className="grid grid-2" style={{ gap: '24px', alignItems: 'start' }}>
        {/* Settings */}
        <div className="card">
          <h2 className="card-title">Settings</h2>
          <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>
            Stored in the database; saving restarts the server if it is running. Values from{' '}
            <code>.env</code> are used as defaults.
          </p>

          <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Port</label>
          <input
            type="number"
            value={port}
            onChange={(e) => {
              formTouched.current = true;
              setPort(e.target.value);
            }}
            style={{ width: '140px', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', marginBottom: '16px' }}
          />

          <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>
            Auth token (Bearer) — required for HTTP mode
          </label>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
            <input
              type={showToken ? 'text' : 'password'}
              value={authToken}
              onChange={(e) => {
                formTouched.current = true;
                setAuthToken(e.target.value);
              }}
              placeholder="empty = no auth (unsafe)"
              style={{ flex: 1, minWidth: '220px', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontFamily: 'monospace' }}
            />
            <button onClick={() => setShowToken((v) => !v)} style={btnSmall}>
              {showToken ? 'Hide' : 'Show'}
            </button>
            <button
              onClick={() => {
                formTouched.current = true;
                setAuthToken(randomToken());
                setShowToken(true);
              }}
              style={btnSmall}
            >
              Generate
            </button>
            <button onClick={() => copy(authToken, 'token')} disabled={!authToken} style={btnSmall}>
              {copied === 'token' ? 'Copied ✓' : 'Copy'}
            </button>
          </div>
          {!authToken && (
            <div style={{ fontSize: '12px', color: '#b45309', marginBottom: '12px' }}>
              ⚠️ Required — the server refuses to start without a token, since the endpoint exposes all data.
            </div>
          )}

          <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, margin: '16px 0 4px' }}>
            Public URL — required for the claude.ai connector
          </label>
          <input
            type="text"
            value={publicUrl}
            onChange={(e) => {
              formTouched.current = true;
              setPublicUrl(e.target.value);
            }}
            placeholder="https://api.fasting.am"
            style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', boxSizing: 'border-box' }}
          />
          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
            The HTTPS origin the reverse proxy serves this on. OAuth metadata and redirects are built from it,
            so it must match the URL you give the connector.
          </div>

          <button
            onClick={saveSettings}
            disabled={busy}
            style={{
              marginTop: '12px',
              background: '#3b82f6',
              color: '#fff',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '8px',
              fontWeight: 600,
              cursor: busy ? 'wait' : 'pointer',
            }}
          >
            Save settings
          </button>
          {saveMsg && <span style={{ marginLeft: '12px', color: '#10b981', fontSize: '14px' }}>{saveMsg}</span>}

          <h2 className="card-title" style={{ marginTop: '28px' }}>Available tools</h2>
          <div style={{ fontSize: '13px', lineHeight: 1.7 }}>
            {TOOL_GROUPS.map(({ group, tools }) => (
              <div key={group} style={{ marginBottom: '6px' }}>
                <strong>{group}:</strong>{' '}
                {tools.map((t, i) => (
                  <span key={t}>
                    <code style={{ background: '#f3f4f6', padding: '1px 5px', borderRadius: '4px' }}>{t}</code>
                    {i < tools.length - 1 ? ' ' : ''}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Connection info */}
        <div className="card">
          <h2 className="card-title">How to connect Claude</h2>

          <h3 style={h3Style}>Claude Code</h3>
          <p style={pStyle}>
            Already configured via <code>.mcp.json</code> in the repo root (stdio — no need to start the HTTP
            server). Open the project and approve the <code>kkal-admin</code> server on first use.
          </p>

          <h3 style={h3Style}>Claude Desktop</h3>
          <p style={pStyle}>
            Add to <code>~/Library/Application Support/Claude/claude_desktop_config.json</code>:
          </p>
          <pre style={preStyle}>
            {desktopConfig}
            <button onClick={() => copy(desktopConfig, 'desktop')} style={{ ...btnSmall, position: 'absolute', top: '8px', right: '8px' }}>
              {copied === 'desktop' ? 'Copied ✓' : 'Copy'}
            </button>
          </pre>

          <h3 style={h3Style}>claude.ai custom connector (HTTP)</h3>
          <p style={pStyle}>
            The server listens on <code>127.0.0.1</code> only, so the port is never exposed directly — put it
            behind the existing HTTPS host. Add to the nginx server block:
          </p>
          <pre style={preStyle}>
            {`location ~ ^/(mcp|authorize|token|register|revoke|\\.well-known/oauth-) {\n    proxy_pass http://127.0.0.1:${state?.settings?.port};\n    proxy_http_version 1.1;\n    proxy_set_header Host $host;\n    proxy_set_header X-Forwarded-Proto $scheme;\n    proxy_buffering off;\n    proxy_read_timeout 300s;\n}`}
          </pre>
          <p style={pStyle}>
            OAuth needs its endpoints at the origin root, which is why the block covers more than{' '}
            <code>/mcp</code>. Check those paths are free on that host first.
          </p>
          <p style={pStyle}>
            Then in claude.ai → Settings → Connectors → Add custom connector, paste{' '}
            <code>{(state?.settings?.publicUrl || 'https://your-host') + '/mcp'}</code> as the server URL and
            leave the OAuth fields empty — Claude registers itself automatically. It then opens a login page
            where you paste the token above, once. Requires this server <strong>running</strong> (toggle above)
            and the Public URL set correctly.
          </p>

          <h3 style={h3Style}>Data sources</h3>
          <p style={pStyle}>
            Analytics tools call this admin API (auto-login); direct-SQL tools read MySQL and work even when the
            admin API is down. Everything is strictly read-only and password hashes are stripped.
          </p>
        </div>
      </div>

      {/* Logs */}
      <div className="card" style={{ marginTop: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 className="card-title" style={{ marginBottom: 0 }}>Logs</h2>
          <button onClick={() => load(false)} style={btnSmall}>
            Refresh
          </button>
        </div>
        <pre
          style={{
            ...preStyle,
            maxHeight: '300px',
            overflowY: 'auto',
            marginTop: '12px',
          }}
        >
          {state?.logs?.length
            ? state.logs.map((l) => `${l.at.slice(11, 19)}  ${l.line}`).join('\n')
            : 'No logs yet — start the server to see output.'}
        </pre>
      </div>
    </div>
  );
};

const btnSmall = {
  padding: '6px 12px',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  background: '#fff',
  color: '#374151',
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: 500,
};

const h3Style = { fontSize: '14px', fontWeight: 700, margin: '14px 0 6px' };
const pStyle = { fontSize: '13px', color: '#374151', lineHeight: 1.6, margin: '0 0 8px' };
const preStyle = {
  position: 'relative',
  background: '#111827',
  color: '#e5e7eb',
  padding: '12px',
  borderRadius: '8px',
  fontSize: '12px',
  overflowX: 'auto',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-all',
};

export default McpSettings;
